import type { LaborRangePreset } from '@/components/payroll/laborAnalyticsControls'
import { isTotalSeriesKey } from '@/components/payroll/laborChartSeries'
import {
  fetchLaborRefresh,
  LABOR_STORE_NAMES,
  type LaborChartQuery,
  type LaborChartResponse,
  type LaborPoint,
  type LaborRefreshResponse,
  type LaborSeries,
  type LaborStoreName,
} from '@/services/laborAnalytics'
import {
  loadLaborAnalyticsCacheEntry,
  saveLaborAnalyticsCache,
  type LaborAnalyticsCacheEntry,
} from '@/utils/laborAnalyticsCache'

export const LABOR_ANALYTICS_EXPORT_HEADERS = [
  'date',
  'store',
  'station',
  'regular_hours',
  'overtime_hours',
  'wages',
  'station_sales',
] as const

export type LaborExportCell = string | number
type LaborExportRow = Partial<Record<(typeof LABOR_ANALYTICS_EXPORT_HEADERS)[number], LaborExportCell>>

function exportNumber(value: string | null | undefined): LaborExportCell {
  if (value == null || value === '') return ''
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : value
}

function exportRowFromSlots(slots: LaborExportRow): LaborExportCell[] {
  return LABOR_ANALYTICS_EXPORT_HEADERS.map((header) => slots[header] ?? '')
}

function pointByDate(series: LaborSeries | undefined): Map<string, LaborPoint> {
  const points = new Map<string, LaborPoint>()
  for (const point of series?.points ?? []) {
    points.set(point.date, point)
  }
  return points
}

function seriesByStation(chart: LaborChartResponse | undefined): Map<string, LaborSeries> {
  const series = new Map<string, LaborSeries>()
  for (const entry of chart?.series ?? []) {
    if (isTotalSeriesKey(entry.key)) continue
    series.set(entry.key, entry)
  }
  return series
}

function stationNames(...groups: Array<Map<string, LaborSeries>>): string[] {
  const names = new Set<string>()
  for (const group of groups) {
    for (const name of group.keys()) names.add(name)
  }
  return [...names].sort((a, b) => a.localeCompare(b))
}

function datesInSeries(...groups: Array<Map<string, LaborSeries>>): string[] {
  const dates = new Set<string>()
  for (const group of groups) {
    for (const series of group.values()) {
      for (const point of series.points) dates.add(point.date)
    }
  }
  return [...dates].sort((a, b) => a.localeCompare(b))
}

function buildStoreRows(
  store: LaborStoreName,
  charts: NonNullable<LaborRefreshResponse['charts_by_store']>[string],
): LaborExportCell[][] {
  const regular = seriesByStation(charts.regular_hours_by_station)
  const overtime = seriesByStation(charts.overtime_hours_by_station)
  const wages = seriesByStation(charts.wages_by_station)
  const stationSales = seriesByStation(charts.wages_pct_of_station_sales)
  const stations = stationNames(regular, overtime, wages, stationSales)
  const dates = datesInSeries(regular, overtime, wages, stationSales)
  const regularPoints = new Map(
    [...regular.entries()].map(([station, series]) => [station, pointByDate(series)]),
  )
  const overtimePoints = new Map(
    [...overtime.entries()].map(([station, series]) => [station, pointByDate(series)]),
  )
  const wagesPoints = new Map(
    [...wages.entries()].map(([station, series]) => [station, pointByDate(series)]),
  )
  const salesPoints = new Map(
    [...stationSales.entries()].map(([station, series]) => [station, pointByDate(series)]),
  )

  const rows: LaborExportCell[][] = []
  for (const date of dates) {
    for (const station of stations) {
      rows.push(
        exportRowFromSlots({
          date,
          store,
          station,
          regular_hours: exportNumber(regularPoints.get(station)?.get(date)?.value),
          overtime_hours: exportNumber(overtimePoints.get(station)?.get(date)?.value),
          wages: exportNumber(wagesPoints.get(station)?.get(date)?.value),
          station_sales: exportNumber(salesPoints.get(station)?.get(date)?.denominator),
        }),
      )
    }
  }
  return rows
}

export function buildLaborAnalyticsExportMatrix(
  payload: LaborRefreshResponse,
): { headers: string[]; rows: LaborExportCell[][] } {
  const rows: LaborExportCell[][] = []
  const chartsByStore = payload.charts_by_store
  if (chartsByStore) {
    for (const store of LABOR_STORE_NAMES) {
      const charts = chartsByStore[store]
      if (!charts) continue
      rows.push(...buildStoreRows(store, charts))
    }
  }

  return {
    headers: [...LABOR_ANALYTICS_EXPORT_HEADERS],
    rows,
  }
}

export function buildLaborAnalyticsExportFilename(start: string, end: string): string {
  return `labor-analytics-${start}-${end}.csv`
}

export interface CollectLaborExportPayloadsDeps {
  fetchRefresh?: (query: LaborChartQuery) => Promise<LaborRefreshResponse>
  loadCache?: (query: LaborChartQuery) => LaborAnalyticsCacheEntry | null
  saveCache?: typeof saveLaborAnalyticsCache
  rangePreset?: LaborRangePreset
}

export async function collectLaborExportPayloads(
  start: string,
  end: string,
  deps: CollectLaborExportPayloadsDeps = {},
): Promise<LaborRefreshResponse> {
  const fetchFn = deps.fetchRefresh ?? fetchLaborRefresh
  const loadFn = deps.loadCache ?? loadLaborAnalyticsCacheEntry
  const saveFn = deps.saveCache ?? saveLaborAnalyticsCache
  const rangePreset = deps.rangePreset ?? 'custom'

  const query: LaborChartQuery = {
    stations: [],
    start,
    end,
    timescale: 'L1',
  }

  const cached = loadFn({ stations: [], start, end })
  if (cached?.payload.charts_by_store) {
    return cached.payload
  }

  const payload = await fetchFn(query)
  saveFn({
    rangePreset,
    query,
    payload,
  })
  return payload
}
